import React, { useEffect, useRef, useState } from 'react';
import { Alert, Card, Input, Layout, Table, Tag, Typography, message, Button, Space, Modal, Popconfirm, Select } from 'antd';
import { MailOutlined, DeleteOutlined, CheckCircleOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { collection, getDocs, query, where, getDoc, doc, runTransaction, deleteField } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useAuth } from '../../context/DataContext';
import CustomSeatPicker from '../SelectSeat/CustomSeatPicker';
import ExpiredPaymentRecovery, { canRecoverExpiredPayment } from './ExpiredPaymentRecovery';
import dayjs from 'dayjs';
import apis from '../../apis';
import { useTranslation } from 'react-i18next';
import {
    isDocumentationCustomer, loadDocumentationCustomers, newDocumentationCustomer, saveDocumentationCustomer,
} from '../../services/documentationCustomers';

const { Content } = Layout;
const { Title, Text } = Typography;

const assignmentTierStyles = {
    presto: { label: 'Presto', color: '#EBBC64' },
    allegro: { label: 'Allegro', color: '#91caff' },
    lento: { label: 'Lento', color: '#d3adf7' },
};
const getAssignmentTierStyle = (areaType) => assignmentTierStyles[areaType] || {
    label: areaType ? `${areaType.charAt(0).toUpperCase()}${areaType.slice(1)}` : 'Unspecified tier',
    color: '#ffffff',
};
const renderAssignmentTierHeader = (row, previousRow) => {
    const firstSeat = row.find(Boolean);
    const previousSeat = previousRow?.find(Boolean);
    if (!firstSeat || firstSeat.areaType === previousSeat?.areaType) return null;
    const tier = getAssignmentTierStyle(firstSeat.areaType);
    return (
        <div style={{ width: '100%', textAlign: 'center', padding: '12px 0 4px' }}>
            <Tag style={{ backgroundColor: tier.color, color: '#111', borderColor: tier.color, fontWeight: 700, fontSize: '14px', padding: '4px 16px', marginInlineEnd: 0 }}>
                {tier.label} section
            </Tag>
        </div>
    );
};

const formatRupiah = (amount) =>
    new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(amount);

const isMasterclassTicket = (ticket) => ['master_class', 'masterclass'].includes(String(ticket.id || '').toLowerCase());
const isPaidBooking = (booking) => booking.paymentStatus === 'PAID' || booking.paymentStatus === 'paid';
const getMissingSeatCount = (booking) => {
    if (isDocumentationCustomer(booking) || booking.seatingMode === 'free') return 0;
    const performanceTickets = (booking.tickets || [])
        .filter(ticket => !isMasterclassTicket(ticket))
        .reduce((total, ticket) => total + Number(ticket.quantity || 0), 0);
    return Math.max(0, performanceTickets - (booking.selectedSeatIds || []).length);
};
const isCancellationResolved = status => ['canceled', 'manually_verified_no_active_invoice', 'manual_payment_not_received'].includes(status);
const canSafelyDeleteBooking = (booking) =>
    ((booking.paymentStatus === 'expired' && isCancellationResolved(booking.expiry?.invoiceCancellationStatus))
    || (booking.paymentStatus === 'failed' && booking.checkoutFailure?.cleanupStatus === 'complete'))
    && booking.expiry?.quotaRefundStatus !== 'reconciliation_required'
    && booking.checkoutFailure?.quotaRefundStatus !== 'reconciliation_required';
const adminReleaseReasonLabels = {
    customer_declined: 'adminSeatRelease.reasonCustomerDeclined',
    no_response_after_one_hour: 'adminSeatRelease.reasonNoResponse',
};

const normalizeSearch = value => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
const matchesCustomerSearch = (booking, search) => {
    const searchText = normalizeSearch([
        booking.buyerName, booking.userName, booking.registrantName,
        booking.userEmail, booking.userPhone, booking.id, booking.registrantId,
    ].join(' '));
    return normalizeSearch(search).split(' ').filter(Boolean).every(term => searchText.includes(term));
};

const PublicCustomersList = () => {
    const { t } = useTranslation();
    const { currentEventId } = useAuth();
    const [customers, setCustomers] = useState([]);
    const [documentationCustomers, setDocumentationCustomers] = useState([]);
    const [documentationCustomer, setDocumentationCustomer] = useState(null);
    const [documentationName, setDocumentationName] = useState('');
    const [documentationNote, setDocumentationNote] = useState('');
    const [savingDocumentation, setSavingDocumentation] = useState(false);
    const [documentationLoadError, setDocumentationLoadError] = useState('');
    const customerLoadId = useRef(0);
    const documentationSaveInFlight = useRef(false);
    const [venuesMap, setVenuesMap] = useState({});
    const [loading, setLoading] = useState(false);
    const [customerSearch, setCustomerSearch] = useState('');
    const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
    const [customerPage, setCustomerPage] = useState(1);
    const [noteBooking, setNoteBooking] = useState(null);
    const [noteDraft, setNoteDraft] = useState('');
    const [savingNote, setSavingNote] = useState(false);
    const [alternateEmailBooking, setAlternateEmailBooking] = useState(null);
    const [alternateEmail, setAlternateEmail] = useState('');
    const [alternateEmailError, setAlternateEmailError] = useState('');
    const [sendingAlternateEmail, setSendingAlternateEmail] = useState(false);
    const [recoveringPayment, setRecoveringPayment] = useState(false);

    // Seat Assignment State
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [seatLayout, setSeatLayout] = useState([]);
    const [loadingSeats, setLoadingSeats] = useState(false);
    const [selectedNewSeats, setSelectedNewSeats] = useState([]);
    const [quantityToAssign, setQuantityToAssign] = useState(0);

    const [eventsList, setEventsList] = useState([]);
    const [selectedEventId, setSelectedEventId] = useState('');

    useEffect(() => {
        if (currentEventId && !selectedEventId) {
            setSelectedEventId(currentEventId);
        }
    }, [currentEventId, selectedEventId]);

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const eventsSnap = await getDocs(collection(db, 'events'));
                const eventsData = eventsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                eventsData.sort((a, b) => b.id.localeCompare(a.id));
                setEventsList(eventsData);
            } catch (error) {
                console.error("Error fetching events list:", error);
            }
        };
        fetchEvents();
    }, []);

    useEffect(() => {
        setCustomerSearch('');
        setPaymentStatusFilter('all');
        setCustomerPage(1);
        setNoteBooking(null);
        setNoteDraft('');
        setAlternateEmailBooking(null);
        setAlternateEmail('');
        setAlternateEmailError('');
        setDocumentationCustomer(null);
        setDocumentationName('');
        setDocumentationNote('');
        setDocumentationCustomers([]);
        if (selectedEventId) {
            fetchCustomers();
        }
        return () => { customerLoadId.current += 1; };
    }, [selectedEventId]);

    const fetchCustomers = async () => {
        if (!selectedEventId) return;
        const requestId = ++customerLoadId.current;
        setLoading(true);
        setDocumentationLoadError('');
        try {
            const q = query(
                collection(db, 'publicBookings'), 
                where('eventId', '==', selectedEventId)
            );
            const [querySnapshot, documentationRows] = await Promise.all([
                getDocs(q),
                loadDocumentationCustomers(selectedEventId).catch(error => {
                    if (requestId === customerLoadId.current) {
                        setDocumentationLoadError('Documentation-only customers could not be loaded. Refresh the list to try again.');
                    }
                    console.error('Error fetching documentation customers:', error);
                    return [];
                }),
            ]);
            if (requestId !== customerLoadId.current) return;
            let data = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })).filter(booking => booking.paymentStatus !== 'archived_test');

            // Sort by createdAt desc in memory to avoid requiring a composite index in Firestore
            data.sort((a, b) => {
                const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt || 0);
                const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt || 0);
                return timeB - timeA;
            });

            const eventRef = doc(db, 'events', selectedEventId);
            const eventSnap = await getDoc(eventRef);
            if (requestId !== customerLoadId.current) return;
            if (eventSnap.exists()) {
                const eventData = eventSnap.data();
                if (eventData.venues) {
                    const vMap = {};
                    eventData.venues.forEach(v => { vMap[v.id] = v.label; });
                    setVenuesMap(vMap);
                }
            }

            setCustomers(data);
            setDocumentationCustomers(documentationRows);
        } catch (error) {
            if (requestId !== customerLoadId.current) return;
            console.error('Error fetching customers:', error);
            message.error('Failed to load customers');
        } finally {
            if (requestId === customerLoadId.current) setLoading(false);
        }
    };

    const openDocumentationModal = (record) => {
        setDocumentationCustomer(record || newDocumentationCustomer(selectedEventId));
        setDocumentationName(record?.userName || '');
        setDocumentationNote(record?.adminNote || '');
    };

    const handleSaveDocumentation = async () => {
        if (!documentationCustomer || documentationSaveInFlight.current) return;
        documentationSaveInFlight.current = true;
        setSavingDocumentation(true);
        try {
            const saved = await saveDocumentationCustomer({
                id: documentationCustomer.id,
                eventId: documentationCustomer.eventId,
                expectedRevision: documentationCustomer.revision,
                userName: documentationName,
                adminNote: documentationNote,
            });
            // Invalidate older loads so they cannot erase this successful local save.
            customerLoadId.current += 1;
            setLoading(false);
            setDocumentationCustomers(previous => [saved, ...previous.filter(customer => customer.id !== saved.id)]);
            setDocumentationCustomer(null);
            setDocumentationName('');
            setDocumentationNote('');
            setCustomerSearch('');
            setPaymentStatusFilter('all');
            setCustomerPage(1);
            message.success('Paid documentation customer saved.');
        } catch (error) {
            message.error(error.message || 'Failed to save documentation customer. Please try again.');
        } finally {
            documentationSaveInFlight.current = false;
            setSavingDocumentation(false);
        }
    };

    const openNoteModal = (record) => {
        if (isDocumentationCustomer(record)) {
            openDocumentationModal(record);
            return;
        }
        setNoteBooking(record);
        setNoteDraft(record.adminNote || '');
    };

    const handleSaveNote = async () => {
        if (!noteBooking || savingNote) return;
        const adminNote = noteDraft.trim();
        if (adminNote.length > 2000) {
            message.error('Please keep the note within 2,000 characters.');
            return;
        }

        setSavingNote(true);
        try {
            // Update only this booking's note; never replace payment or inventory fields.
            await runTransaction(db, async (transaction) => {
                const bookingRef = doc(db, 'publicBookings', noteBooking.id);
                const snapshot = await transaction.get(bookingRef);
                if (!snapshot.exists()) throw new Error('This booking no longer exists.');
                const booking = snapshot.data();
                if (booking.eventId !== noteBooking.eventId) {
                    throw new Error('This booking has changed. Refresh the list before editing its note.');
                }
                if ((booking.adminNote || '') !== (noteBooking.adminNote || '')) {
                    throw new Error('Another staff member changed this note. Copy your draft, cancel and refresh the list before trying again.');
                }
                transaction.update(bookingRef, { adminNote: adminNote || deleteField() });
            });
            setCustomers(previous => previous.map(customer => customer.id === noteBooking.id
                ? { ...customer, adminNote } : customer));
            setNoteBooking(null);
            setNoteDraft('');
            message.success(adminNote ? 'Note saved.' : 'Note removed.');
        } catch (error) {
            message.error(error.message || 'Failed to save note. Please try again.');
        } finally {
            setSavingNote(false);
        }
    };

    const openAssignModal = async (record) => {
        if (isDocumentationCustomer(record) || record.seatingMode === 'free') return;
        if (!isPaidBooking(record)) {
            message.error('Seats can only be assigned after payment is confirmed.');
            return;
        }
        const toAssign = getMissingSeatCount(record);

        if (toAssign <= 0) {
            message.info('All seats are already assigned for this booking.');
            return;
        }

        setSelectedBooking(record);
        setQuantityToAssign(toAssign);
        setSelectedNewSeats([]);
        setIsAssignModalOpen(true);
        setLoadingSeats(true);
        setSeatLayout([]);

        try {
            const sessionId = `${record.date}_${record.session}`;
            const seatsCollection = collection(db, `seats${selectedEventId || 'APCS2026'}`);
            const q = query(seatsCollection, where('sessionId', '==', sessionId), where('venueId', '==', record.venue));
            const snapshot = await getDocs(q);
            
            const fetchedSeats = [];
            snapshot.forEach(docSnap => {
                fetchedSeats.push({ id: docSnap.id, ...docSnap.data() });
            });

            // Format layout
            const groupedByArea = fetchedSeats.reduce((acc, seat) => {
                const area = seat.areaType?.toLowerCase();
                if (!acc[area]) acc[area] = [];
                acc[area].push(seat);
                return acc;
            }, {});

            const finalLayouts = {};
            const areaOrder = ['presto', 'allegro', 'lento'];
            const rightSideMax = {
                A: 9, B: 10, C: 10, D: 9, E: 9, F: 11,
                G: 9, H: 9, I: 9, J: 8, K: 8, L: 9, M: 10,
                N: 13, O: 13, P: 13, Q: 13, R: 12, S: 12, T: 12, U: 12
            };

            const areasInDb = Object.keys(groupedByArea);
            const sortedAreas = areasInDb.sort((a, b) => {
                const idxA = areaOrder.indexOf(a.toLowerCase());
                const idxB = areaOrder.indexOf(b.toLowerCase());
                if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                if (idxA !== -1) return -1;
                if (idxB !== -1) return 1;
                return a.localeCompare(b);
            });

            sortedAreas.forEach(area => {
                if (groupedByArea[area]) {
                    const rowsObject = groupedByArea[area].reduce((acc, seat) => {
                        const row = seat.row;
                        if (!acc[row]) acc[row] = [];
                        acc[row].push(seat);
                        return acc;
                    }, {});

                    const sortedRowKeys = Object.keys(rowsObject).sort((a, b) => a.localeCompare(b));

                    finalLayouts[area] = sortedRowKeys.map(rowKey => {
                        const rowSeats = rowsObject[rowKey];
                        rowSeats.sort((a, b) => b.number - a.number);
                        
                        const mappedRow = rowSeats.map(seat => {
                            const tier = getAssignmentTierStyle(area);
                            let tooltipText = `Status: ${seat.status.toUpperCase()}`;
                            if (seat.status === 'booked' && seat.assignedTo) {
                                tooltipText = `Booked by: ${seat.assignedTo.registrantName || seat.assignedTo.userName} (${seat.assignedTo.userEmail || ''})`;
                            }
                            
                            return {
                                id: seat.id,
                                number: seat.number,
                                row: seat.row,
                                areaType: area,
                                color: tier.color,
                                isReserved: seat.status !== 'available',
                                status: seat.status,
                                tooltip: (
                                    <div style={{ textAlign: 'center' }}>
                                        <strong style={{ display: 'block', marginBottom: '4px' }}>Seat {seat.seatLabel}</strong>
                                        <span style={{ display: 'block' }}>{tier.label} section</span>
                                        <span>{tooltipText}</span>
                                    </div>
                                ),
                                statusColor: seat.status === 'booked' ? '#ff4d4f' : seat.status === 'reserved' ? '#bfbfbf' : seat.status === 'locked' ? '#faad14' : undefined,
                                seatLabel: seat.seatLabel
                            };
                        });

                        if (record.venue === 'Venue1') {
                            const splitSeatNumber = rightSideMax[rowKey];
                            if (splitSeatNumber) {
                                const splitIndex = mappedRow.findIndex(s => s.number === splitSeatNumber);
                                if (splitIndex !== -1) {
                                    mappedRow.splice(splitIndex, 0, null, null);
                                }
                            }
                        }

                        return mappedRow;
                    });
                }
            });

            const finalLayoutArray = [];
            areaOrder.forEach(area => {
                if (finalLayouts[area]) {
                    finalLayoutArray.push(...finalLayouts[area]);
                }
            });

            setSeatLayout(finalLayoutArray);

        } catch (error) {
            console.error("Error fetching seat layout:", error);
            message.error("Failed to load seat map.");
        } finally {
            setLoadingSeats(false);
        }
    };

    const handleSelectSeat = (seat) => {
        setSelectedNewSeats(prev => [...prev, seat]);
    };

    const handleDeselectSeat = (seat) => {
        setSelectedNewSeats(prev => prev.filter(s => s.id !== seat.id));
    };

    const handleAssignSeatsSubmit = async () => {
        if (selectedNewSeats.length !== quantityToAssign) {
            message.error(`Please select exactly ${quantityToAssign} seats.`);
            return;
        }

        setLoading(true);
        try {
            // The protected backend transaction validates payment, session, tier, quantity,
            // canonical chair ownership and physical aliases before booking the seats.
            await apis.publicTicket.assignSeats(selectedBooking.id, selectedNewSeats.map(seat => seat.id));

            message.success('Seats successfully assigned!');
            setIsAssignModalOpen(false);
            fetchCustomers();
        } catch (error) {
            console.error("Assignment error:", error);
            message.error(`Failed to assign seats: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleResendEmail = async (bookingId) => {
        try {
            message.loading({ content: 'Resending email...', key: 'resendEmail' });
            const response = await apis.publicTicket.resendPublicTicketEmail({ bookingId });
            if (response.error) throw new Error(response.error.data?.message || 'Failed to resend email.');
            message.success({ content: 'Email resent successfully!', key: 'resendEmail' });
            
            // Update the local state to show email as sent
            setCustomers(prev => prev.map(c => c.id === bookingId ? { ...c, emailSent: true } : c));
        } catch (error) {
            console.error('Error resending email:', error);
            message.error({ content: error.response?.data?.message || 'Failed to resend email.', key: 'resendEmail' });
        }
    };

    const openAlternateEmailModal = (record) => {
        if (!isPaidBooking(record)) return;
        setAlternateEmailBooking(record);
        setAlternateEmail('');
        setAlternateEmailError('');
    };

    const handleSendAlternateEmail = async () => {
        if (!alternateEmailBooking || sendingAlternateEmail) return;
        const recipient = alternateEmail.trim();
        if (recipient.length > 254 || !/^[^\s@<>,;]+@[^\s@<>,;]+\.[^\s@<>,;]+$/.test(recipient)) {
            setAlternateEmailError('Enter one valid email address.');
            return;
        }
        setAlternateEmailError('');
        setSendingAlternateEmail(true);
        try {
            await apis.publicTicket.sendPublicTicketEmailToAddress(alternateEmailBooking.id, recipient);
            message.success(`Confirmation email sent to ${recipient}.`);
            setAlternateEmailBooking(null);
            setAlternateEmail('');
        } catch (error) {
            setAlternateEmailError(error.message || 'Sending could not be confirmed. Check delivery before retrying.');
        } finally {
            setSendingAlternateEmail(false);
        }
    };

    const handleMarkPaid = async (record) => {
        try {
            message.loading({ content: 'Marking as paid...', key: 'markPaid' });
            if (record.paymentMode === 'manual') {
                const token = await auth.currentUser?.getIdToken();
                if (!token) throw new Error('Please sign in again.');
                const response = await apis.publicTicket.markManualPaid(record.id, token);
                if (response.error) throw new Error(response.error.data?.message || 'Could not confirm manual payment.');
                message.success({ content: 'Manual payment confirmed. Confirmation email attempted.', key: 'markPaid' });
                await fetchCustomers();
                return;
            }
            
            await runTransaction(db, async (transaction) => {
                const bookingRef = doc(db, 'publicBookings', record.id);
                const bookingDoc = await transaction.get(bookingRef);
                
                if (!bookingDoc.exists()) {
                    throw new Error("Booking does not exist.");
                }

                const bookingData = bookingDoc.data();

                if (bookingData.paymentStatus !== 'pending') {
                    throw new Error('This booking cannot be marked paid unless it is currently pending and payment has been verified.');
                }
                if (!bookingData.eventId) throw new Error('Booking has no event reference.');

                // Collect all seat IDs
                const seatIdsToReserve = [...new Set([
                    ...(bookingData.selectedSeatIds || []),
                    ...(bookingData.orchestraSelectedSeatIds || [])
                ])];

                // Retrieve all seat docs
                const seatDocs = [];
                for (const seatId of seatIdsToReserve) {
                    const seatRef = doc(db, `seats${bookingData.eventId}`, seatId);
                    const seatSnap = await transaction.get(seatRef);
                    if (!seatSnap.exists()) throw new Error(`Seat ${seatId} no longer exists.`);
                    const seatData = seatSnap.data();
                    if (seatData.status !== 'locked' || seatData.lockedByBookingId !== record.id) {
                        throw new Error(`Seat ${seatData.seatLabel || seatId} is no longer locked by this booking.`);
                    }
                    seatDocs.push(seatSnap);
                }
                const normalizeSeatPart = value => String(value ?? '').trim().toUpperCase();
                const safeDocumentId = value => encodeURIComponent(String(value ?? '')).replace(/%/g, '_');
                const physicalSeatKey = seat => [
                    bookingData.eventId,
                    normalizeSeatPart(seat.venueId),
                    normalizeSeatPart(seat.sessionId),
                    normalizeSeatPart(seat.row),
                    String(seat.number ?? '').trim(),
                ].join('|');
                const physicalSeatKeys = seatDocs.map(seatSnap => physicalSeatKey(seatSnap.data()));
                if (new Set(physicalSeatKeys).size !== physicalSeatKeys.length) {
                    throw new Error('A physical seat can only be finalized once per booking.');
                }
                const ownershipRefs = physicalSeatKeys.map(key =>
                    doc(db, 'ticketSeatOwnership', safeDocumentId(key)));
                const ownershipDocs = [];
                for (const ownershipRef of ownershipRefs) {
                    ownershipDocs.push(await transaction.get(ownershipRef));
                }
                for (let index = 0; index < ownershipDocs.length; index++) {
                    const ownership = ownershipDocs[index];
                    if (ownership.exists() && ownership.data().active !== false
                        && ownership.data().bookingId !== record.id) {
                        throw new Error(`Physical seat ${seatDocs[index].data().seatLabel} is already held by another booking.`);
                    }
                }

                // Update seats
                for (let index = 0; index < seatDocs.length; index++) {
                    const seatSnap = seatDocs[index];
                    transaction.update(seatSnap.ref, {
                        status: 'booked',
                        bookingId: record.id,
                        lockedAt: deleteField(),
                        lockedByBookingId: deleteField(),
                        assignedTo: {
                            userName: bookingData.userName || '',
                            userEmail: bookingData.userEmail || '',
                            registrantName: bookingData.registrantName || ''
                        }
                    });
                    transaction.set(ownershipRefs[index], {
                        eventId: bookingData.eventId,
                        physicalSeatKey: physicalSeatKeys[index],
                        bookingId: record.id,
                        seatId: seatSnap.id,
                        status: 'booked',
                        active: true,
                        updatedAt: new Date(),
                    });
                }

                // Update booking
                transaction.update(bookingRef, {
                    paymentStatus: 'PAID',
                    updatedAt: new Date()
                });
            });

            message.success({ content: 'Marked as PAID successfully! You can now resend the email.', key: 'markPaid' });
            setCustomers(prev => prev.map(c => c.id === record.id ? { ...c, paymentStatus: 'PAID' } : c));
        } catch (error) {
            console.error('Error marking as paid:', error);
            message.error({ content: error.message || 'Failed to mark as paid', key: 'markPaid' });
        }
    };

    const handleCancelManualBooking = async (record) => {
        try {
            const token = await auth.currentUser?.getIdToken();
            if (!token) throw new Error('Please sign in again.');
            const response = await apis.publicTicket.releaseBooking({
                bookingId: record.id,
                reason: 'manual_payment_unpaid',
                note: 'Staff verified that payment was not received.',
                paymentNotReceivedConfirmed: true,
            }, token);
            if (response.error || !response.data?.released) {
                throw new Error(response.error?.data?.message || 'Could not cancel manual booking.');
            }
            message.success('Unpaid manual booking canceled and inventory released.');
            await fetchCustomers();
        } catch (error) {
            message.error(error.message || 'Could not cancel manual booking.');
        }
    };

    const handleResendManualInstructions = async (record) => {
        try {
            const token = await auth.currentUser?.getIdToken();
            if (!token) throw new Error('Please sign in again.');
            const response = await apis.publicTicket.resendManualInstructions(record.id, token);
            if (response.error || !response.data?.sent) {
                throw new Error(response.error?.data?.message || 'Could not send payment instructions.');
            }
            message.success('Payment instructions sent.');
            setCustomers(previous => previous.map(customer => customer.id === record.id
                ? { ...customer, manualInstructionsEmailSent: true } : customer));
        } catch (error) {
            message.error(error.message || 'Could not send payment instructions.');
        }
    };

    const handleDeleteBooking = async (bookingId) => {
        try {
            message.loading({ content: 'Deleting booking...', key: 'deleteBooking' });
            await runTransaction(db, async (transaction) => {
                const bookingRef = doc(db, 'publicBookings', bookingId);
                const bookingDoc = await transaction.get(bookingRef);
                
                if (!bookingDoc.exists()) {
                    throw new Error("Booking does not exist.");
                }

                const bookingData = bookingDoc.data();
                if (!canSafelyDeleteBooking(bookingData)) {
                    throw new Error('This booking still has payment or inventory state. Reconcile it through the cancellation process instead of deleting it.');
                }

                // Inventory and quota were already released by the confirmed cancellation cleanup.
                transaction.delete(bookingRef);
            });

            message.success({ content: 'Booking deleted successfully', key: 'deleteBooking' });
            fetchCustomers();
        } catch (error) {
            console.error('Error deleting booking:', error);
            message.error({ content: error.message || 'Failed to delete booking', key: 'deleteBooking' });
        }
    };

    const columns = [
        {
            title: 'Record ID',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'Type',
            key: 'recordType',
            render: (_, record) => isDocumentationCustomer(record)
                ? <Tag color="purple">Documentation only</Tag>
                : <Tag>Ticket booking</Tag>,
        },
        {
            title: 'Name',
            dataIndex: 'userName',
            key: 'userName',
        },
        {
            title: 'Email',
            dataIndex: 'userEmail',
            key: 'userEmail',
            render: value => value || '-',
        },
        {
            title: 'Phone',
            dataIndex: 'userPhone',
            key: 'userPhone',
            render: value => value || '-',
        },
        {
            title: 'Venue',
            dataIndex: 'venue',
            key: 'venue',
            render: (venue, record) => isDocumentationCustomer(record) ? '-' : venuesMap[venue] || 'APCS Venue',
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: value => value || '-',
        },
        {
            title: 'Session',
            dataIndex: 'session',
            key: 'session',
            render: value => value || '-',
        },
        {
            title: 'Total Amount',
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            render: (amount, record) => isDocumentationCustomer(record) ? 'Recorded offline' : formatRupiah(amount),
        },
        {
            title: 'Payment Status',
            dataIndex: 'paymentStatus',
            key: 'paymentStatus',
            render: (status, record) => {
                let color = 'default';
                if (status === 'PAID' || status === 'paid') color = 'green';
                else if (status === 'pending') color = 'orange';
                else if (status === 'expired') color = 'red';
                else if (status === 'failed') color = 'red';
                const receiptStatus = !isPaidBooking(record) ? record.paymentReceipt?.status : null;

                return (
                    <Space direction="vertical" size={4}>
                        <Tag color={color}>
                            {status ? status.toUpperCase() : 'UNKNOWN'}
                        </Tag>
                        {receiptStatus === 'pending' && (
                            <Tag color="gold">Paid callback received — reconciling</Tag>
                        )}
                        {receiptStatus === 'needs_review' && (
                            <Tag color="volcano">Payment received — needs review ({record.paymentReceipt.reason})</Tag>
                        )}
                        {!isDocumentationCustomer(record) && canRecoverExpiredPayment(record) && (
                            <ExpiredPaymentRecovery key={`${record.eventId}:${record.id}`} booking={record}
                                onRecovered={fetchCustomers} onBusyChange={setRecoveringPayment} disabled={recoveringPayment} />
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'Seats',
            dataIndex: 'performanceSeatLabels',
            key: 'performanceSeatLabels',
            render: (seats, record) => {
                if (isDocumentationCustomer(record)) return <Tag color="purple">No seat</Tag>;
                const missingSeatCount = getMissingSeatCount(record);
                return (
                    <Space direction="vertical" size={4}>
                        <span>{(seats && seats.length > 0) ? seats.join(', ') : '-'}</span>
                        {isPaidBooking(record) && missingSeatCount > 0 && (
                            <Tag color="gold" title="Expand this booking and use Assign Missing Seats to complete its numbered seat assignment.">
                                Needs seat assignment ({missingSeatCount})
                            </Tag>
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'Note',
            dataIndex: 'adminNote',
            key: 'adminNote',
            width: 240,
            render: (note, record) => (
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{note || '-'}</div>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => openNoteModal(record)}
                        style={{ color: '#EBBC64', borderColor: '#EBBC64', backgroundColor: '#1E1E1E' }}
                    >
                        {note ? 'Edit Note' : 'Add Note'}
                    </Button>
                </Space>
            ),
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (timestamp) => timestamp ? dayjs(timestamp.toDate?.() ?? timestamp).format('DD MMM YYYY HH:mm') : '-',
        },
        {
            title: 'APCS Email Status',
            dataIndex: 'emailSent',
            key: 'emailSent',
            render: (emailSent, record) => {
                if (isDocumentationCustomer(record)) return <Tag>NOT APPLICABLE</Tag>;
                if (record.paymentMode === 'manual' && record.paymentStatus === 'pending') {
                    return record.manualInstructionsEmailSent
                        ? <Tag color="blue">PAYMENT INFO SENT</Tag>
                        : <Tag color="orange">PAYMENT INFO NEEDS SENDING</Tag>;
                }
                if (!isPaidBooking(record)) {
                    return record.paymentStatus === 'pending'
                        ? <Tag color="gold" title="The APCS confirmation email is sent after payment is confirmed. Invoice and reservation emails are separate.">AWAITING PAYMENT</Tag>
                        : <Tag color="default" title="An unpaid terminal booking does not receive a paid confirmation email.">NOT APPLICABLE</Tag>;
                }
                if (emailSent === true) return <Tag color="green" title="APCS paid confirmation email sent.">SENT</Tag>;
                if (emailSent === false) return <Tag color="red" title="The APCS paid confirmation email could not be sent. Use Resend to retry.">FAILED</Tag>;
                return <Tag color="gold" title="APCS paid confirmation email has no recorded send result. Use Resend if needed.">NOT SENT</Tag>;
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => isDocumentationCustomer(record) ? (
                <Button size="small" icon={<EditOutlined />} onClick={() => openDocumentationModal(record)}
                    style={{ color: '#EBBC64', borderColor: '#EBBC64', backgroundColor: '#1E1E1E' }}>
                    Edit Customer
                </Button>
            ) : (
                <Space>
                    <Button 
                        type="primary" 
                        icon={<MailOutlined />} 
                        size="small" 
                        onClick={() => handleResendEmail(record.id)}
                        disabled={record.paymentStatus !== 'PAID' && record.paymentStatus !== 'paid'}
                    >
                        Resend
                    </Button>
                    <Button
                        size="small"
                        icon={<MailOutlined />}
                        disabled={!isPaidBooking(record)}
                        onClick={() => openAlternateEmailModal(record)}
                        style={{ color: '#EBBC64', borderColor: '#EBBC64', backgroundColor: '#1E1E1E' }}
                    >
                        Send to Another Email
                    </Button>
                    {record.paymentStatus === 'pending' && (
                        <Popconfirm
                            title="Mark as Paid?"
                            description={record.paymentMode === 'manual'
                                ? 'Confirm that staff verified payment. This will finalize the booking and attempt its confirmation email.'
                                : 'This will confirm the booking and assign the seats. Continue?'}
                            onConfirm={() => handleMarkPaid(record)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button 
                                type="default" 
                                icon={<CheckCircleOutlined />} 
                                size="small" 
                                style={{ color: '#52c41a', borderColor: '#52c41a' }}
                            >
                                Mark Paid
                            </Button>
                        </Popconfirm>
                    )}
                    {record.paymentStatus === 'pending' && record.paymentMode === 'manual' && (
                        <Button size="small" onClick={() => handleResendManualInstructions(record)}>
                            {record.manualInstructionsEmailSent ? 'Resend Payment Info' : 'Send Payment Info'}
                        </Button>
                    )}
                    {record.paymentStatus === 'pending' && record.paymentMode === 'manual' && (
                        <Popconfirm
                            title="Cancel unpaid manual booking?"
                            description="Confirm that staff checked payment and no payment was received. This releases all reserved places."
                            onConfirm={() => handleCancelManualBooking(record)}
                            okText="Cancel booking"
                            cancelText="Keep booking"
                        >
                            <Button size="small" danger>Cancel Unpaid</Button>
                        </Popconfirm>
                    )}
                    {canSafelyDeleteBooking(record) && (
                        <Popconfirm
                            title="Delete reconciled booking?"
                            description="Inventory and complimentary quota were already released after confirmed cancellation."
                            onConfirm={() => handleDeleteBooking(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                            >
                                Delete
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        }
    ];

    const expandedRowRender = (record) => {
        if (isDocumentationCustomer(record)) {
            return (
                <div style={{ padding: 16, backgroundColor: '#1E1E1E', borderRadius: 8 }}>
                    <Text strong>Paid documentation purchase</Text>
                    <p>Payment was received outside APCS. Documentation only; no seat or event admission is included.</p>
                    <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{record.adminNote || 'No note added.'}</div>
                </div>
            );
        }
        return (
            <div style={{ margin: '0 24px', padding: '16px', background: '#f9f9f9', borderRadius: '8px' }}>
                <Title level={5} style={{ marginTop: 0 }}>Order Details</Title>
                {record.paymentReconciliation && (
                    <Alert type="success" showIcon message={<span style={{ color: '#EBBC64' }}>Payment reconciled after expiry</span>}
                        style={{ marginBottom: 16, backgroundColor: '#1E1E1E', borderColor: '#EBBC64' }}
                        description={<div style={{ color: '#e5cc92' }}>
                            <div>Verified by: {record.paymentReconciliation.reconciledByEmail || '-'}</div>
                            <div>Paper.id invoice: {record.paymentReconciliation.invoiceId || '-'}</div>
                            {record.paymentReconciliation.note && <div>Review note: {record.paymentReconciliation.note}</div>}
                        </div>} />
                )}
                {record.paymentStatus === 'failed' && (
                    <div style={{ marginBottom: 16 }}>
                        <Text type="danger">Checkout failed. This booking cannot be marked paid.</Text>
                        <br />
                        <Text>Invoice cancellation: {{ canceled: 'Confirmed', pending: 'Pending', failed: 'Needs reconciliation', unknown: 'Unknown — needs reconciliation', not_requested: 'Invoice not requested', manually_verified_no_active_invoice: t('adminSeatRelease.manualNoInvoiceStatus') }[record.checkoutFailure?.invoiceCancellationStatus] || 'Unknown — needs reconciliation'}</Text>
                        {record.checkoutFailure?.quotaRefundStatus === 'reconciliation_required' && (
                            <p><Text type="danger">Complimentary quota requires reconciliation.</Text></p>
                        )}
                    </div>
                )}
                {record.adminRelease && (
                    <Alert
                        style={{
                            marginBottom: 16,
                            background: '#151515',
                            borderColor: '#c8a95b',
                            color: '#f7f0df',
                        }}
                        type="success"
                        showIcon
                        icon={<CheckCircleOutlined style={{ color: '#c8a95b' }} />}
                        message={<span style={{ color: '#e7cc83' }}>{t('adminSeatRelease.publicStatusTitle')}</span>}
                        description={(
                            <div style={{ color: '#f7f0df' }}>
                                <div>{t('adminSeatRelease.publicReason')}: {adminReleaseReasonLabels[record.adminRelease.reason] ? t(adminReleaseReasonLabels[record.adminRelease.reason]) : record.adminRelease.reason || '-'}</div>
                                <div>{t('adminSeatRelease.publicReleasedBy')}: {record.adminRelease.releasedByEmail || '-'}</div>
                                {record.adminRelease.note && <div>{t('adminSeatRelease.publicNote')}: {record.adminRelease.note}</div>}
                            </div>
                        )}
                    />
                )}
                <div style={{ display: 'flex', gap: '48px', flexWrap: 'wrap' }}>
                    <div style={{ minWidth: '200px' }}>
                        <Text strong>Tickets:</Text>
                        <ul style={{ paddingLeft: '20px', marginTop: '8px' }}>
                            {(record.tickets || []).filter(t => t.quantity > 0).map((t, idx) => (
                                <li key={idx}>{t.quantity}x {t.name}</li>
                            ))}
                            {(!record.tickets || record.tickets.filter(t => t.quantity > 0).length === 0) && (
                                <li>None</li>
                            )}
                        </ul>
                    </div>

                    {(record.addOnIds?.length > 0 || record.freeMasterclassCount > 0) && (
                        <div style={{ minWidth: '200px' }}>
                            <Text strong>Add-ons & Extras:</Text>
                            <ul style={{ paddingLeft: '20px', marginTop: '8px' }}>
                                {(() => {
                                    if (!record.addOnIds) return null;
                                    const counts = {};
                                    record.addOnIds.forEach(id => { counts[id] = (counts[id] || 0) + 1; });
                                    return Object.entries(counts).map(([id, qty]) => (
                                        <li key={id}>{qty}x {id.replace(/_/g, ' ')}</li>
                                    ));
                                })()}
                                {record.freeMasterclassCount > 0 && (
                                    <li>{record.freeMasterclassCount}x Master Class (Free)</li>
                                )}
                            </ul>
                        </div>
                    )}

                    <div style={{ minWidth: '200px' }}>
                        <Text strong>Seats & Assignments:</Text>
                        <ul style={{ paddingLeft: '20px', marginTop: '8px' }}>
                            <li>
                                <strong>Performance:</strong> {(record.performanceSeatLabels || []).join(', ') || '-'}
                            </li>
                            {(record.orchestraSeatLabels?.length > 0 || record.complimentaryTickets > 0) && (
                                <li>
                                    <strong>Orchestra (Free):</strong> {(record.orchestraSeatLabels || []).join(', ') || 'Auto-Assigned'}
                                </li>
                            )}
                            {record.ticketingVersion === 2 && record.orchestraAttendanceTickets > 0 && <li><strong>Orchestra:</strong> {record.orchestraAttendanceTickets} ticket(s); performer places counted once across purchases. Manage the whole group in Orchestra Assignments.</li>}
                            {record.registrantName && (
                                <li>
                                    <strong>Paying For:</strong> {record.registrantName}
                                </li>
                            )}
                        </ul>
                        {(() => {
                            if (record.seatingMode === 'free') return <Tag color="gold">Free seating</Tag>;
                            const missingSeatCount = getMissingSeatCount(record);
                            const isPaid = isPaidBooking(record);
                            
                            if (missingSeatCount > 0 && isPaid) {
                                return (
                                    <Button 
                                        type="primary" 
                                        size="small" 
                                        style={{ marginTop: '16px' }}
                                        onClick={() => openAssignModal(record)}
                                    >
                                        Assign Missing Seats ({missingSeatCount})
                                    </Button>
                                );
                            } else if (missingSeatCount > 0 && !isPaid) {
                                return (
                                    <div style={{ marginTop: '16px', color: '#faad14' }}>
                                        <i>Cannot assign seats (Payment Status is {record.paymentStatus || 'unknown'})</i>
                                    </div>
                                );
                            }
                            return null;
                        })()}
                    </div>
                </div>
            </div>
        );
    };

    const filteredCustomers = [...customers, ...documentationCustomers].sort((a, b) => {
        const milliseconds = timestamp => timestamp?.toMillis?.() ?? (timestamp ? new Date(timestamp).getTime() : 0);
        return milliseconds(b.createdAt) - milliseconds(a.createdAt);
    }).filter(booking =>
        matchesCustomerSearch(booking, customerSearch)
        && (paymentStatusFilter === 'all' || String(booking.paymentStatus || '').toLowerCase() === paymentStatusFilter));
    const bookingsNeedingSeats = customers.filter(booking => isPaidBooking(booking) && getMissingSeatCount(booking) > 0).length;

    return (
        <Content style={{ padding: '24px', minHeight: 280 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Title level={2}>Public Customers</Title>
                    <Text type="secondary">Ticket bookings and paid documentation-only customers for the selected event.</Text>
                </div>
                <div>
                    <span style={{ marginRight: '8px', fontWeight: 500 }}>Filter by Event:</span>
                    <Select 
                        value={selectedEventId} 
                        disabled={savingNote || sendingAlternateEmail || savingDocumentation || recoveringPayment}
                        onChange={(value) => {
                            setSelectedEventId(value);
                            setCustomers([]);
                            setDocumentationCustomers([]);
                        }} 
                        style={{ width: 200 }}
                    >
                        {eventsList.map(ev => (
                            <Select.Option key={ev.id} value={ev.id}>{ev.title || ev.id}</Select.Option>
                        ))}
                    </Select>
                </div>
            </div>

            <Card style={{ marginTop: '24px' }}>
                {documentationLoadError && <Alert type="warning" showIcon message={documentationLoadError} style={{ marginBottom: 16 }} />}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                    <Button icon={<PlusOutlined />} disabled={!selectedEventId || loading || savingDocumentation}
                        onClick={() => openDocumentationModal()}
                        style={{ color: '#121212', backgroundColor: '#EBBC64', borderColor: '#EBBC64' }}>
                        Add Documentation Customer
                    </Button>
                    <Button onClick={fetchCustomers} disabled={loading || savingDocumentation}>Refresh</Button>
                    <Input
                        aria-label="Search public ticket bookings"
                        placeholder="Search reservation or performer name, email, phone or ID"
                        allowClear
                        value={customerSearch}
                        onChange={event => {
                            setCustomerSearch(event.target.value);
                            setCustomerPage(1);
                        }}
                        style={{ width: 480, maxWidth: '100%' }}
                    />
                    <Select
                        aria-label="Filter by payment status"
                        value={paymentStatusFilter}
                        onChange={value => {
                            setPaymentStatusFilter(value);
                            setCustomerPage(1);
                        }}
                        options={[
                            { value: 'all', label: 'All Payment Statuses' },
                            { value: 'expired', label: 'Expired' },
                            { value: 'pending', label: 'Pending' },
                            { value: 'paid', label: 'Paid' },
                        ]}
                        style={{ width: 220, maxWidth: '100%', backgroundColor: '#1E1E1E', color: '#EBBC64' }}
                    />
                </div>
                {bookingsNeedingSeats > 0 && (
                    <div style={{ marginBottom: 16 }}>
                        <Tag color="gold" title="Total for the selected event, across all payment statuses, search results and pages.">
                            {bookingsNeedingSeats} {bookingsNeedingSeats === 1 ? 'booking needs' : 'bookings need'} seat assignment
                        </Tag>
                    </div>
                )}
                <Table
                    columns={columns}
                    dataSource={filteredCustomers}
                    rowKey={record => `${isDocumentationCustomer(record) ? 'documentation' : 'booking'}:${record.id}`}
                    loading={loading}
                    pagination={{ current: customerPage, pageSize: 10, onChange: setCustomerPage, showSizeChanger: false }}
                    locale={{ emptyText: customerSearch.trim() || paymentStatusFilter !== 'all' ? 'No customers match your search or payment status filter.' : 'No customers for the selected event.' }}
                    scroll={{ x: 1200 }}
                    expandable={{ expandedRowRender }}
                />
            </Card>

            <Modal
                title={documentationCustomer?.revision ? 'Edit Documentation Customer' : 'Add Documentation Customer'}
                open={Boolean(documentationCustomer)}
                onOk={handleSaveDocumentation}
                onCancel={() => {
                    if (documentationSaveInFlight.current) return;
                    setDocumentationCustomer(null);
                    setDocumentationName('');
                    setDocumentationNote('');
                }}
                okText="Save Customer"
                confirmLoading={savingDocumentation}
                cancelButtonProps={{ disabled: savingDocumentation }}
                closable={!savingDocumentation}
                maskClosable={!savingDocumentation}
                keyboard={!savingDocumentation}
                okButtonProps={{ style: { backgroundColor: '#EBBC64', borderColor: '#EBBC64', color: '#121212' } }}
            >
                <p><Tag color="green">PAID</Tag><Tag color="purple">Documentation only · No seat</Tag></p>
                <p><Text type="secondary">Event: {documentationCustomer?.eventId}</Text></p>
                <p>Add only documentation purchases with payment already verified as received outside APCS. This record includes no event admission.</p>
                <label htmlFor="documentation-customer-name">Customer name (required)</label>
                <Input id="documentation-customer-name" value={documentationName}
                    onChange={event => setDocumentationName(event.target.value)}
                    maxLength={200} disabled={savingDocumentation} aria-required="true"
                    style={{ marginTop: 8, marginBottom: 16, backgroundColor: '#1E1E1E', color: '#e5cc92', borderColor: '#EBBC64' }} />
                <label htmlFor="documentation-customer-note">Note (optional)</label>
                <Input.TextArea id="documentation-customer-note" value={documentationNote}
                    onChange={event => setDocumentationNote(event.target.value)}
                    autoSize={{ minRows: 4, maxRows: 10 }} maxLength={2000} showCount disabled={savingDocumentation}
                    style={{ marginTop: 8, marginBottom: 20, backgroundColor: '#1E1E1E', color: '#e5cc92', borderColor: '#EBBC64' }} />
            </Modal>

            <Modal
                title="Send Confirmation to Another Email"
                open={Boolean(alternateEmailBooking)}
                onOk={handleSendAlternateEmail}
                onCancel={() => {
                    if (sendingAlternateEmail) return;
                    setAlternateEmailBooking(null);
                    setAlternateEmail('');
                    setAlternateEmailError('');
                }}
                okText="Send Email"
                confirmLoading={sendingAlternateEmail}
                cancelButtonProps={{ disabled: sendingAlternateEmail }}
                closable={!sendingAlternateEmail}
                maskClosable={!sendingAlternateEmail}
                keyboard={!sendingAlternateEmail}
                okButtonProps={{ style: { backgroundColor: '#EBBC64', borderColor: '#EBBC64', color: '#121212' } }}
            >
                <p><Text strong>{alternateEmailBooking?.buyerName || alternateEmailBooking?.userName || 'Buyer'}</Text><br />
                    Booking: {alternateEmailBooking?.id}<br />
                    Saved email: {alternateEmailBooking?.userEmail || '-'}</p>
                <p>This sends the booking confirmation to the address below. The saved buyer email and its APCS Email Status stay unchanged. This action sends a new copy even if a confirmation was sent before.</p>
                <label htmlFor="alternate-confirmation-email">Recipient email</label>
                <Input
                    id="alternate-confirmation-email"
                    type="email"
                    placeholder="recipient@example.com"
                    value={alternateEmail}
                    onChange={event => {
                        setAlternateEmail(event.target.value);
                        setAlternateEmailError('');
                    }}
                    onPressEnter={handleSendAlternateEmail}
                    maxLength={254}
                    disabled={sendingAlternateEmail}
                    status={alternateEmailError ? 'error' : undefined}
                    aria-describedby={alternateEmailError ? 'alternate-email-error' : undefined}
                    style={{ marginTop: 8, backgroundColor: '#1E1E1E', color: '#e5cc92', borderColor: '#EBBC64' }}
                />
                {alternateEmailError && <div id="alternate-email-error" role="alert" style={{ marginTop: 8 }}><Text type="danger">{alternateEmailError}</Text></div>}
            </Modal>

            <Modal
                title={`Note for ${noteBooking?.buyerName || noteBooking?.userName || 'buyer'}`}
                open={Boolean(noteBooking)}
                onOk={handleSaveNote}
                onCancel={() => {
                    if (savingNote) return;
                    setNoteBooking(null);
                    setNoteDraft('');
                }}
                okText="Save Note"
                confirmLoading={savingNote}
                cancelButtonProps={{ disabled: savingNote }}
                closable={!savingNote}
                maskClosable={!savingNote}
                keyboard={!savingNote}
                okButtonProps={{ style: { backgroundColor: '#EBBC64', borderColor: '#EBBC64', color: '#121212' } }}
            >
                <Text type="secondary">Booking: {noteBooking?.id}. Leave the note empty and save to remove it.</Text>
                <Input.TextArea
                    aria-label="Buyer note"
                    placeholder="Enter a note for this buyer's booking"
                    value={noteDraft}
                    onChange={event => setNoteDraft(event.target.value)}
                    autoSize={{ minRows: 4, maxRows: 10 }}
                    maxLength={2000}
                    showCount
                    disabled={savingNote}
                    style={{ marginTop: 12, marginBottom: 20, backgroundColor: '#1E1E1E', color: '#e5cc92', borderColor: '#EBBC64' }}
                />
            </Modal>

            <Modal
                title={`Assign Seats for ${selectedBooking?.buyerName || selectedBooking?.userName}`}
                open={isAssignModalOpen}
                onCancel={() => setIsAssignModalOpen(false)}
                width={1000}
                onOk={handleAssignSeatsSubmit}
                okText="Save Assignment"
                confirmLoading={loading}
                okButtonProps={{ disabled: selectedNewSeats.length !== quantityToAssign }}
                destroyOnClose
            >
                <div style={{ marginBottom: '16px' }}>
                    <Text>
                        Please select <strong>{quantityToAssign}</strong> seats. 
                        Currently selected: {selectedNewSeats.length}
                    </Text>
                    {selectedBooking && (
                        <div style={{ marginTop: '8px' }}>
                            <Text type="secondary">
                                Note: This user purchased: {(selectedBooking.tickets || []).filter(ticket => !isMasterclassTicket(ticket) && ticket.quantity > 0).map(ticket => `${ticket.quantity}x ${ticket.name}`).join(', ')}.
                                Please select seats corresponding to their purchased tier.
                            </Text>
                        </div>
                    )}
                </div>
                <div style={{ overflowX: 'auto', backgroundColor: '#f0f2f5', padding: '24px', borderRadius: '8px' }}>
                    {loadingSeats ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>Loading Seat Map...</div>
                    ) : (
                        <CustomSeatPicker 
                            layout={seatLayout} 
                            selectedSeats={selectedNewSeats} 
                            onSelect={handleSelectSeat} 
                            onDeselect={handleDeselectSeat} 
                            maxSeats={quantityToAssign} 
                            renderRowHeader={renderAssignmentTierHeader}
                        />
                    )}
                </div>
            </Modal>
        </Content>
    );
};

export default PublicCustomersList;
